// fitImage.js — режим сжатия фото, включаемый только по «умной ссылке» (?fit_image).
// Положить в корень фронтенда (рядом с App.js).
//
// Логика:
//   • enableFitImageMode()  — вызывается при входе по ссылке с ?fit_image (App.js).
//   • disableFitImageMode() — вызывается перед отправкой заказа (ModalCart.jsx).
//   • isFitImageMode()      — доступна ли фича сейчас (показывать ли галочку, сжимать ли).
//   • getCompressOn/setCompressOn — состояние галочки (по умолчанию ВКЛ, пока режим активен).
//   • compressImage(file)   — само сжатие (уменьшение + JPEG).
//
// Флаги живут в sessionStorage: действуют на вкладку-сессию (переживают перезагрузку
// после редиректа /user/... → /), и гасятся при оформлении заказа.

const MODE_KEY = "fit_image_mode";   // фича доступна (пришли по ссылке с ?fit_image)
const ON_KEY   = "fit_image_on";     // текущее состояние галочки «сжимать»

export function enableFitImageMode() {
  try {
    sessionStorage.setItem(MODE_KEY, "1");
    if (sessionStorage.getItem(ON_KEY) === null) {
      sessionStorage.setItem(ON_KEY, "1");   // по умолчанию ВКЛ
    }
  } catch (e) {}
}

export function disableFitImageMode() {
  try {
    sessionStorage.removeItem(MODE_KEY);
    sessionStorage.removeItem(ON_KEY);
  } catch (e) {}
}

export function isFitImageMode() {
  try {
    return sessionStorage.getItem(MODE_KEY) === "1";
  } catch (e) {
    return false;
  }
}

export function getCompressOn() {
  try {
    const v = sessionStorage.getItem(ON_KEY);
    return v === null ? true : v === "1";   // по умолчанию ВКЛ, когда режим активен
  } catch (e) {
    return true;
  }
}

export function setCompressOn(on) {
  try {
    sessionStorage.setItem(ON_KEY, on ? "1" : "0");
  } catch (e) {}
}

/**
 * Уменьшает и пере-жимает картинку в JPEG. Возвращает новый File.
 * Бросает Error, если браузер не смог декодировать файл (например RAW).
 */
export async function compressImage(file, { maxDim = 3000, quality = 0.85 } = {}) {
  if (!file || !file.type || !file.type.startsWith("image/")) return file;
  let bitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch (e) {
    throw new Error("decode_failed");   // RAW/битый формат — декодировать нечем
  }
  try {
    const { width, height } = bitmap;
    const scale = Math.min(1, maxDim / Math.max(width, height));
    const w = Math.max(1, Math.round(width * scale));
    const h = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, w, h);
    // Check rendered pixels, not the filename: PNG/WebP may contain alpha.
    let transparent = false;
    for (let y = 0; y < h && !transparent; y += 64) {
      const pixels = ctx.getImageData(0, y, w, Math.min(64, h - y)).data;
      for (let i = 3; i < pixels.length; i += 4) {
        if (pixels[i] < 255) { transparent = true; break; }
      }
    }
    const mime = transparent ? "image/png" : "image/jpeg";
    const blob = await new Promise((res) => canvas.toBlob(res, mime, quality));
    if (!blob) return file;
    const newName = file.name.replace(/\.[^.]+$/, "") + (transparent ? ".png" : ".jpg");
    const out = new File([blob], newName, { type: mime, lastModified: Date.now() });
    return out.size < file.size ? out : file;   // маленький JPEG не раздуваем
  } finally {
    if (bitmap.close) bitmap.close();
  }
}